import React, { useState } from 'react';
import {
  Box,
  Avatar,
  Typography,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import { CommunityMember } from '../../services/communityService';
import {
  canRemoveMember,
  canChangeRole,
  canTransferOwnership,
  roleLabel,
} from '../../utils/communityPermissions';

function avatarColor(id: string): string {
  const colors = ['#d32f2f', '#1976d2', '#388e3c', '#f57c00', '#7b1fa2', '#0288d1', '#455a64'];
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = id.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .substring(0, 2);
}

interface Props {
  member: CommunityMember;
  actorRole?: 'owner' | 'moderator' | 'member';
  actorClientId: string;
  onRemove: (member: CommunityMember) => void;
  onToggleModerator: (member: CommunityMember, makeModerator: boolean) => void;
  onMakeOwner?: (member: CommunityMember) => void;
}

export const CommunityMemberRow: React.FC<Props> = ({
  member,
  actorRole,
  actorClientId,
  onRemove,
  onToggleModerator,
  onMakeOwner,
}) => {
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const displayName = member.display_name || member.client_id;
  const showRemove = canRemoveMember(
    actorRole,
    member.role,
    actorClientId,
    member.client_id
  );
  const showRoleActions = canChangeRole(actorRole) && member.client_id !== actorClientId;
  const showMakeOwner =
    canTransferOwnership(actorRole) &&
    member.client_id !== actorClientId &&
    member.role !== 'owner' &&
    Boolean(onMakeOwner);
  const hasActions = showRemove || showRoleActions || showMakeOwner;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        py: 1,
        px: 0.5,
      }}
    >
      <Avatar
        src={member.avatar_url}
        sx={{ width: 40, height: 40, bgcolor: avatarColor(member.client_id) }}
      >
        {getInitials(displayName)}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontWeight: 500,
            fontSize: '0.9rem',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {displayName}
          {member.client_id === actorClientId ? ' (You)' : ''}
        </Typography>
        <Chip
          label={roleLabel(member.role)}
          size="small"
          sx={{
            mt: 0.25,
            height: 20,
            fontSize: '0.7rem',
            bgcolor: member.role === 'owner' ? '#e8f0fe' : '#f1f3f4',
            color: member.role === 'owner' ? '#1967d2' : '#5f6368',
          }}
        />
      </Box>
      {hasActions && (
        <>
          <IconButton size="small" onClick={(e) => setAnchor(e.currentTarget)} sx={{ color: '#606060' }}>
            <MoreVertIcon fontSize="small" />
          </IconButton>
          <Menu
            anchorEl={anchor}
            open={Boolean(anchor)}
            onClose={() => setAnchor(null)}
            PaperProps={{ sx: { borderRadius: 2, minWidth: 180 } }}
          >
            {showRoleActions && member.role === 'member' && (
              <MenuItem
                onClick={() => {
                  setAnchor(null);
                  onToggleModerator(member, true);
                }}
              >
                <ListItemIcon>
                  <AdminPanelSettingsIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Make admin" />
              </MenuItem>
            )}
            {showRoleActions && member.role === 'moderator' && (
              <MenuItem
                onClick={() => {
                  setAnchor(null);
                  onToggleModerator(member, false);
                }}
              >
                <ListItemIcon>
                  <AdminPanelSettingsIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Remove admin" />
              </MenuItem>
            )}
            {showMakeOwner && (
              <MenuItem
                onClick={() => {
                  setAnchor(null);
                  onMakeOwner?.(member);
                }}
              >
                <ListItemIcon>
                  <WorkspacePremiumIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Make owner" />
              </MenuItem>
            )}
            {showRemove && (
              <MenuItem
                onClick={() => {
                  setAnchor(null);
                  onRemove(member);
                }}
                sx={{ color: '#d32f2f' }}
              >
                <ListItemIcon>
                  <PersonRemoveIcon fontSize="small" sx={{ color: '#d32f2f' }} />
                </ListItemIcon>
                <ListItemText primary="Remove from community" />
              </MenuItem>
            )}
          </Menu>
        </>
      )}
    </Box>
  );
};
